import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import type { AttentionPanelData } from '@/types/dashboards'
import FigureValue, { figureToneClass } from './FigureValue'
import { useDrillDown } from './useDrillDown'

interface AttentionTableProps {
  data: AttentionPanelData
  className?: string
}

/**
 * The ranked Head Attention list.
 *
 * Rank comes from the server's scoring rule (design spec §6) — this renders
 * the order it is given and never re-ranks client-side, so what the operator
 * sees is what the inspectable module constant produced.
 */
export default function AttentionTable({ data, className }: AttentionTableProps) {
  const navigate = useNavigate()
  const { open } = useDrillDown()

  if (data.items.length === 0) {
    return (
      <p className={clsx('text-[11px] text-stone-500', className)}>
        Nothing is ranked above the attention threshold.
      </p>
    )
  }

  return (
    <div className={clsx('flex min-h-0 flex-col gap-1.5', className)}>
      {data.items.map((item) => (
        <div
          key={item.id}
          className="surface-panel flex items-center gap-2.5 rounded-md px-2.5 py-2"
        >
          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border border-amber-400/20 bg-amber-400/[0.06] font-mono text-[10px] text-amber-300">
            {item.rank}
          </span>

          <div className="min-w-0 flex-1">
            <div className="truncate text-[11.5px] font-medium text-stone-100" title={item.title}>
              {item.title}
            </div>
            {item.subtitle && (
              <div className="truncate text-[10px] text-stone-500">{item.subtitle}</div>
            )}
          </div>

          {item.figure && (
            <span
              className={clsx(
                'flex-shrink-0 font-mono text-[11px] tabular-nums',
                figureToneClass(item.tone ?? item.figure.tone),
              )}
            >
              <FigureValue figure={item.figure} align="right" />
            </span>
          )}

          {item.dueLabel && (
            <span className="w-16 flex-shrink-0 text-right font-mono text-[10px] text-stone-500">
              {item.dueLabel}
            </span>
          )}

          {item.action && (
            <button
              type="button"
              onClick={() => {
                if (item.action?.drill) open(item.action.drill)
                else if (item.action?.href) navigate(item.action.href)
              }}
              disabled={!item.action.drill && !item.action.href}
              className="flex-shrink-0 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
            >
              {item.action.label}
            </button>
          )}
        </div>
      ))}
      {data.footnote && (
        <p className="text-[10px] leading-relaxed text-stone-500">{data.footnote}</p>
      )}
    </div>
  )
}
